import React from 'react';
import Icon from '../../../components/AppIcon';

const ComparisonSummary = ({ wooConfig, shopifyConfig, currency, vatEnabled }) => {
  const formatCurrency = (amount) => {
    const symbol = currency === 'USD' ? '$' : 'R';
    return `${symbol}${amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const calculateVAT = (amount) => {
    return vatEnabled ? amount * 0.15 : 0;
  };

  const calculateWooTotal = () => {
    let total = 0;
    total += parseFloat(wooConfig?.hostingCost || 0);
    total += parseFloat(wooConfig?.shippingCost || 0);
    if (wooConfig?.customDevelopment) total += 2000;
    const vat = calculateVAT(total);
    return total + vat;
  };

  const calculateShopifyTotal = () => {
    const plans = { basic: 700, standard: 1900, advanced: 7200 };
    let total = plans?.[shopifyConfig?.selectedPlan] || 0;
    const vat = calculateVAT(total);
    return total + vat;
  };

  const wooTotal = calculateWooTotal();
  const shopifyTotal = calculateShopifyTotal();
  const difference = Math.abs(wooTotal - shopifyTotal);
  const cheaper = wooTotal < shopifyTotal ? 'WooCommerce' : 'Shopify';
  const percentageDiff = shopifyTotal > 0 ? ((difference / shopifyTotal) * 100)?.toFixed(1) : 0;

  const calculateBreakEven = () => {
    const avgOrderValue = 500;
    const monthlyOrders = 100;
    const wooGatewayFee = (wooConfig?.gatewayPercentage || 2.9) / 100;
    const shopifyPlans = { basic: 0.02, standard: 0.01, advanced: 0.006 };
    const shopifyFee = shopifyPlans?.[shopifyConfig?.selectedPlan] || 0.02;

    const wooTransactionCost = avgOrderValue * wooGatewayFee * monthlyOrders;
    const shopifyTransactionCost = avgOrderValue * shopifyFee * monthlyOrders;

    const wooTotalCost = wooTotal + wooTransactionCost;
    const shopifyTotalCost = shopifyTotal + shopifyTransactionCost;

    return {
      wooOrders: Math.ceil(wooTotalCost / avgOrderValue),
      shopifyOrders: Math.ceil(shopifyTotalCost / avgOrderValue),
      wooRevenue: wooTotalCost,
      shopifyRevenue: shopifyTotalCost
    };
  };

  const breakEven = calculateBreakEven();

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="BarChart3" size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Cost Comparison Summary</h3>
          <p className="text-sm text-muted-foreground">Total cost of ownership analysis</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="ShoppingCart" size={16} className="text-purple-600" />
            <span className="text-sm font-medium text-foreground">WooCommerce</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(wooTotal)}</p>
          <p className="text-xs text-muted-foreground mt-1">Monthly total</p>
        </div>

        <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Store" size={16} className="text-green-600" />
            <span className="text-sm font-medium text-foreground">Shopify</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(shopifyTotal)}</p>
          <p className="text-xs text-muted-foreground mt-1">Monthly total</p>
        </div>

        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingDown" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Savings</span>
          </div>
          <p className="text-2xl font-bold text-primary">{formatCurrency(difference)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {cheaper} is {percentageDiff}% cheaper
          </p>
        </div>
      </div>
      <div className="space-y-4">
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Target" size={16} className="text-primary" />
            Break-Even Analysis
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">WooCommerce Break-Even</p>
              <p className="text-lg font-semibold text-foreground">
                {breakEven?.wooOrders} orders
              </p>
              <p className="text-xs text-muted-foreground">
                Revenue: {formatCurrency(breakEven?.wooRevenue)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Shopify Break-Even</p>
              <p className="text-lg font-semibold text-foreground">
                {breakEven?.shopifyOrders} orders
              </p>
              <p className="text-xs text-muted-foreground">
                Revenue: {formatCurrency(breakEven?.shopifyRevenue)}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-accent/5 border border-accent/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Lightbulb" size={20} className="text-accent mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-1">Recommendation</h4>
              <p className="text-sm text-muted-foreground">
                {cheaper === 'WooCommerce' ?'WooCommerce offers lower monthly costs but requires technical expertise for maintenance. Ideal for businesses with development resources.' :'Shopify provides a managed solution with built-in features and support. Better for businesses prioritizing ease of use and scalability.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonSummary;